import React, { Fragment, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { useAuth } from "../context/AuthContext";
import UserSelection from "./UserSelection";
import CertSelection from "./CertSelection";
import DateTimePicker from "react-datetime-picker";
import { addDays } from "date-fns";
import {
  ButtonModalFormSaveClose,
  ButtonModalFormSaveCloseThirdAction,
} from "./ButtonModalSelection";
import { UserCertificationDelete } from "../modals/UserCertificationDelete";
import { UserCertHistory } from "./UserCertHistory";

export const UserCertCreation = ({
  cert,
  defaultUser,
  onComplete,
  close = null,
  type,
}) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [certValid, setCertValid] = useState(false);
  const [needExpiration, setNeedExpiration] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [issued, setIssued] = useState(new Date());
  const [issuedValid, setIssuedValid] = useState(false);
  const [expiration, setExpiration] = useState(null);
  const [expirationValid, setExpirationValid] = useState(false);
  const [notes, setNotes] = useState("");
  const [notesValid, setNotesValid] = useState(false);
  const [disableUserSelection, setDisableUserSelection] = useState(false);
  const [hasResponseError, setHasResponseError] = useState(false);
  const [
    userCertificationDeleteModalVisable,
    setUserCertificationDeleteModalVisable,
  ] = useState(false);
  const [contentVisible, setContentVisible] = useState(true);

  useEffect(() => {
    if (defaultUser !== null && defaultUser !== undefined) {
      setUser(defaultUser);
      setDisableUserSelection(true);
    }
  }, [defaultUser]);

  useEffect(() => {
    const valid = certificate !== null && certificate !== undefined;
    setCertValid(valid);
    setHasResponseError(false);

    const expires =
      certificate?.defaultLifeTimeDays !== null &&
      certificate?.defaultLifeTimeDays !== undefined;
    setNeedExpiration(expires);
  }, [certificate]);

  useEffect(() => {
    var valid =
      certValid && issuedValid && notesValid && user !== null && user !== undefined;
    if (needExpiration) valid = valid && expirationValid;

    setSaveDisabled(!valid);
  }, [certValid, user, issuedValid, expirationValid, needExpiration, notesValid]);

  useEffect(() => {
    setNotesValid(notes !== null && notes.trim() !== "");
  }, [notes]);

  useEffect(() => {
    const valid = issued !== null && issued !== undefined;
    setIssuedValid(valid);

    if (
      needExpiration &&
      valid &&
      certificate?.defaultLifeTimeDays !== null &&
      certificate?.defaultLifeTimeDays !== undefined
    ) {
      const defaultExpiration = addDays(
        issued,
        certificate?.defaultLifeTimeDays,
      );
      setExpiration(defaultExpiration);
    }
  }, [issued, needExpiration, certificate]);

  useEffect(() => {
    const valid = expiration !== null && expiration !== undefined;
    setExpirationValid(valid);
  }, [expiration]);

  useEffect(() => {
    // A history note describes THIS change, so always start blank on open.
    setNotes("");
  }, [cert]);

  useEffect(() => {
    if (cert === null || cert === undefined) return;

    setCertificate(cert?.certificate);
    setUser(cert?.user);
    setIssued(new Date(cert?.issued));

    if (cert?.expiration) setExpiration(new Date(cert?.expiration));
  }, [cert]);

  const onSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      var exp = needExpiration ? expiration?.toISOString() : null;
      const payload = {
        userId: user?.id,
        certificationId: certificate?.id,
        issued: issued?.toISOString(),
        expiration: exp,
        notes: notes?.trim(),
      };

      var params = {};
      const isUpdate = cert?.id !== null && cert?.id !== undefined;
      if (isUpdate) params["user_cert_id"] = cert?.id;

      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: params,
      };

      const response = await authAxios().post(
        "/api/backoffice/certs/" +
          (isUpdate ? "user_edit_cert" : "user_create_cert"),
        payload,
        config,
      );

      setLoading(false);
      if (
        response?.request?.status !== null &&
        response?.request?.status !== undefined &&
        response?.request?.status === 409
      ) {
        setHasResponseError(true);
        return;
      }
      if (onComplete !== null && onComplete !== undefined)
        onComplete(response.data);
    } catch (err) {
      setLoading(false);
      console.log("error:");
      console.log(err);
    }
  };

  const deleteModalClose = () => {
    setUserCertificationDeleteModalVisable(false);
    setContentVisible(true);
  };

  const openDeleteModal = () => {
    setUserCertificationDeleteModalVisable(true);
    setContentVisible(false);
  };

  const displayContent = () => {
    if (!contentVisible) return null;
    return (
      <Fragment>
        <Container fluid>
          <Row>
            <Col xs={12} lg={7}>
              <Form onSubmit={onSave}>
            <Form.Group className="mb-3" controlId="formCert">
              <Row>
                <Col xs lg="2">
                  <Form.Label>Certificate</Form.Label>
                </Col>
                <Col xs lg="4">
                  <Form.Control
                    as={CertSelection}
                    defaultValue={certificate}
                    onSelected={setCertificate}
                    isInvalid={hasResponseError}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    style={hasResponseError ? { display: "block" } : null}
                  >
                    User already has {certificate?.name} assigned to them.
                  </Form.Control.Feedback>
                </Col>
              </Row>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formUser">
              <Row>
                <Col xs lg="2">
                  <Form.Label>User</Form.Label>
                </Col>
                <Col xs lg="4">
                  <UserSelection
                    defaultValue={user}
                    onSelected={setUser}
                    disabled={disableUserSelection}
                  />
                </Col>
              </Row>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formIssued">
              <Row>
                <Col xs lg="2">
                  <Form.Label>Issued</Form.Label>
                </Col>
                <Col xs lg="4">
                  <Form.Control
                    as={DateTimePicker}
                    format={"dd/MM/yyyy"}
                    value={issued}
                    onChange={(value) => {
                      setIssued(value);
                    }}
                  />
                </Col>
              </Row>
            </Form.Group>

            {needExpiration ? (
              <Form.Group className="mb-3" controlId="formExpiration">
                <Row>
                  <Col xs lg="2">
                    <Form.Label>Expiration</Form.Label>
                  </Col>
                  <Col xs lg="4">
                    <Form.Control
                      as={DateTimePicker}
                      format={"dd/MM/yyyy"}
                      value={expiration}
                      onChange={(value) => {
                        setExpiration(value);
                      }}
                    />
                  </Col>
                </Row>
              </Form.Group>
            ) : null}

                <Form.Group className="mb-3" controlId="formNote">
                  <Row>
                    <Col xs lg="2">
                      <Form.Label>Note</Form.Label>
                    </Col>
                    <Col xs lg="8">
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        isInvalid={!notesValid}
                        required
                        placeholder="Reason for this change (required)"
                      />
                      <Form.Control.Feedback type="invalid">
                        A note is required.
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                </Form.Group>
              </Form>
            </Col>
            <Col xs={12} lg={5}>
              <UserCertHistory
                userId={user?.id}
                certificationId={certificate?.id}
              />
            </Col>
          </Row>
        </Container>
        {type === "edit" ? (
          <ButtonModalFormSaveCloseThirdAction
            saveName={"Save"}
            close={close}
            formSave={onSave}
            loading={loading || saveDisabled}
            thirdActionName={"Delete"}
            thirdActionAction={openDeleteModal}
          />
        ) : (
          <ButtonModalFormSaveClose
            saveName={"Save"}
            close={close}
            formSave={onSave}
            loading={loading || saveDisabled}
          />
        )}
      </Fragment>
    );
  };

  return (
    <Fragment>
      {displayContent()}
      <UserCertificationDelete
        visible={userCertificationDeleteModalVisable}
        close={deleteModalClose}
        user={user}
        certification={cert}
        onComplete={onComplete}
      />
    </Fragment>
  );
};
